import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { User, LogOut, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface UserProfileProps {
  className?: string;
}

export const UserProfile: React.FC<UserProfileProps> = ({ className = "" }) => {
  const { user, logout, isLoading } = useAuth0();
  const [isExpanded, setIsExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className={`${className} animate-pulse`}>
        <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full" />
      </div>
    );
  }

  if (!user) return null;

  const handleLogout = () => {
    logout({ 
      logoutParams: { 
        returnTo: window.location.origin 
      } 
    });
  };

  // Get display name
  const displayName = user.name || user.email?.split('@')[0] || 'User';

  return (
    <div className={`relative ${className}`}>
      {/* User Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center space-x-2 rounded-full bg-stone-200/80 dark:bg-gray-800/80 backdrop-blur-sm border hover:bg-stone-300 dark:hover:bg-gray-700 transition-colors px-3 py-2 text-gray-800 dark:text-gray-100"
      >
        <User className="w-4 h-4 text-gray-700 dark:text-gray-300" />
        <span className="text-sm font-medium max-w-24 truncate text-gray-800 dark:text-gray-100">
          {displayName}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-3 h-3 text-gray-700 dark:text-gray-300" />
        ) : (
          <ChevronDown className="w-3 h-3 text-gray-700 dark:text-gray-300" />
        )}
      </Button>

      {/* Simple Dropdown */}
      <AnimatePresence>
        {isExpanded && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsExpanded(false)}
            />
            
            {/* Dropdown Menu */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 top-full mt-2 z-50"
            >
              <Card className="w-48 border bg-white dark:bg-gray-800 shadow-lg border-gray-200 dark:border-gray-600">
                <CardContent className="p-2">
                  {/* User Info */}
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {user.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user.email}
                    </p>
                  </div>
                  
                  {/* Sign Out Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/30 mt-1"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};